import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';

import { capitalizeFirstLetter } from '@core/utils/capitalize-first-letter.util';
import { PlinkoHistory } from '../interfaces';
import { CostComponent } from '@core/components/cost/cost.component';
import { ModalService } from '@features/modals/services/modal.service';

interface Props {
  modalService: ModalService;
}

export const plinkoHistoryTableConfig = ({
  modalService,
}: Props): ColumnDef<PlinkoHistory>[] => [
  {
    accessorFn: (row) => row.id,
    id: 'id',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.value,
    id: 'value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Value',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: cell.getValue() as number,
          balanceType: cell.row.original.balancetype,
          gap: 4,
          color: 'var(--yellow)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.rows,
    id: 'rows',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Rows',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.risk,
    id: 'risk',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Risk',
      };
    },
    cell: (cell) => capitalizeFirstLetter(cell.getValue() as string),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.serialid,
    id: 'serialid',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Serial ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.nonce,
    id: 'nonce',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Nonce',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => {
      const value = row.value;
      const multiplier = row.multiplier;
      return value * multiplier - value;
    },
    id: 'profit',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Profit',
      };
    },
    cell: (cell) => {
      const profit = cell.getValue() as number;
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: profit,
          balanceType: cell.row.original.balancetype,
          gap: 4,
          color: profit >= 0 ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.multiplier,
    id: 'multiplier',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Multiplier',
      };
    },
    cell: (cell) => `x${cell.getValue()}`,
    enableSorting: true,
  },
  {
    id: 'action',
    header: () => 'Action',
    cell: (cell) =>
      flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `/provably-fair/plinko/${cell.row.original.id}`,
        },
        outputs: {
          handleClick: () => {
            modalService.close();
          },
        },
      }),
    enableSorting: false,
  },
];
